import { Upload, Users, Share2, BarChart2 } from "lucide-react"
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useScrollReveal } from '../../hooks/useScrollReveal'

const stepIcons = [
  <Upload size={26} className="text-green-600" />,
  <Users size={26} className="text-green-600" />,
  <Share2 size={26} className="text-green-600" />,
  <BarChart2 size={26} className="text-green-600" />,
]

const stepDelays = ["delay-100", "delay-200", "delay-300", "delay-400"]

interface Step {
  number: string
  title: string
  description: string
}

export default function HowItWorks() {
  const { t, i18n } = useTranslation()
  const headerRef = useScrollReveal<HTMLDivElement>('how-header', i18n.language)
  const gridRef = useScrollReveal<HTMLDivElement>('how-grid', i18n.language, { threshold: 0.08 })
  const ctaRef = useScrollReveal<HTMLDivElement>('how-cta', i18n.language)

  const steps = t('landing.howItWorks.steps', { returnObjects: true }) as Step[]

  return (
    <section id="how-it-works" className="py-20 bg-green-50 dark:bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className="reveal-up text-center mb-16">
          <p
            className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {t('landing.howItWorks.sectionLabel')}
          </p>
          <h2
            className="text-3xl sm:text-4xl font-bold text-gray-700 dark:text-white"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {t('landing.howItWorks.headline')}
          </h2>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, i) => (
            <div
              key={step.number}
              className={`reveal-up ${stepDelays[i]} flex flex-col items-center text-center`}
            >
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-md mx-auto mb-3">
                {step.number}
              </div>
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
                {stepIcons[i]}
              </div>
              <h3
                className="text-base font-bold text-gray-900 dark:text-white mb-2 leading-tight"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {step.title}
              </h3>
              <p
                className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {step.description}
              </p>
            </div>
          ))}
        </div>

        <div ref={ctaRef} className="reveal-up delay-200 text-center mt-16">
          <Link
            to={`/${i18n.language}/create`}
            className="text-base font-semibold text-gray-700 dark:text-gray-200 hover:text-green-600 dark:hover:text-green-400 transition-colors"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {t('landing.howItWorks.ctaBottom')}
          </Link>
        </div>
      </div>
    </section>
  )
}
